import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Close,
  Upload,
  FileDownload,
  CheckCircle,
  Error,
  Warning
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';

export interface ImportRow {
  barcode: string;
  quantity: number;
  source?: string;
  destination?: string;
  boxBarcode?: string;
  [key: string]: any; // For any additional columns
}

interface ExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (rows: ImportRow[]) => Promise<void>;
  title: string;
  expectedColumns: {
    required: string[];
    optional: string[];
  };
  templateFileName?: string;
}

const ExcelImportDialog: React.FC<ExcelImportDialogProps> = ({
  open,
  onClose,
  onImport,
  title,
  expectedColumns,
  templateFileName
}) => {
  const [importData, setImportData] = useState<ImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setFileName(file.name);
    setError(null);
    setValidationErrors([]);
    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      if (jsonData.length < 2) {
        // throw new Error('Excel file must contain at least a header row and one data row');
      }

      // Parse headers (first row)
      const headers = jsonData[0].map((h: any) => String(h).toLowerCase().trim());
      
      // Validate required columns
      const missingColumns = expectedColumns.required.filter(col => 
        !headers.some(h => h.includes(col.toLowerCase()) || col.toLowerCase().includes(h))
      );

      if (missingColumns.length > 0) {
        // throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      // Find column indices
      const getColumnIndex = (columnName: string): number => {
        return headers.findIndex(h => 
          h.includes(columnName.toLowerCase()) || 
          columnName.toLowerCase().includes(h)
        );
      };

      const barcodeIndex = getColumnIndex('barcode');
      const quantityIndex = getColumnIndex('quantity');
      const sourceIndex = getColumnIndex('source');
      const destinationIndex = getColumnIndex('destination');
      const boxBarcodeIndex = getColumnIndex('box');

      // Parse data rows
      const rows: ImportRow[] = [];
      const errors: string[] = [];

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0 || !row[barcodeIndex]) continue;

        const barcode = String(row[barcodeIndex] || '').trim();
        const quantity = parseInt(String(row[quantityIndex] || '1'));

        if (!barcode) {
          errors.push(`Row ${i + 1}: Barcode is required`);
          continue;
        }

        if (isNaN(quantity) || quantity <= 0) {
          errors.push(`Row ${i + 1}: Invalid quantity`);
          continue;
        }

        const importRow: ImportRow = {
          barcode,
          quantity,
        };

        // Add optional fields if present
        if (sourceIndex >= 0 && row[sourceIndex]) {
          importRow.source = String(row[sourceIndex]).trim();
        }
        if (destinationIndex >= 0 && row[destinationIndex]) {
          importRow.destination = String(row[destinationIndex]).trim();
        }
        if (boxBarcodeIndex >= 0 && row[boxBarcodeIndex]) {
          importRow.boxBarcode = String(row[boxBarcodeIndex]).trim();
        }

        rows.push(importRow);
      }

      if (errors.length > 0) {
        setValidationErrors(errors);
      }

      if (rows.length === 0) {
      }

      setImportData(rows);
    } catch (err: any) {
      setError(err.message || 'Failed to parse Excel file');
    } finally {
      setLoading(false);
    }
  }, [expectedColumns]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: false
  });

  const generateTemplate = () => {
    if (!templateFileName) return;
    
    const headers = [...expectedColumns.required, ...expectedColumns.optional];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      headers,
      // Add sample data row
      headers.map(h => {
        switch (h.toLowerCase()) {
          case 'barcode': return '1234567890';
          case 'quantity': return 1;
          case 'source': return 'Warehouse A';
          case 'destination': return 'Warehouse B';
          case 'boxbarcode': return 'BOX123';
          default: return '';
        }
      })
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, templateFileName);
  };

  const handleImport = async () => {
    if (importData.length === 0) return;
    
    setLoading(true);
    try {
      await onImport(importData);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setImportData([]);
    setError(null);
    setValidationErrors([]);
    setFileName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Instructions */}
          <Alert severity="info">
            <Typography variant="body2">
              Upload an Excel file with the following columns:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Required:</strong> {expectedColumns.required.join(', ')}
            </Typography>
            {expectedColumns.optional.length > 0 && (
              <Typography variant="body2">
                <strong>Optional:</strong> {expectedColumns.optional.join(', ')}
              </Typography>
            )}
          </Alert>

          {/* Template Download */}
          {templateFileName && (
            <Box>
              <Button 
                startIcon={<FileDownload />}
                onClick={generateTemplate}
                variant="outlined"
                size="small"
              >
                Download Template
              </Button>
            </Box>
          )}

          {/* File Upload */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'primary.50' : 'grey.50',
              '&:hover': {
                bgcolor: 'primary.50',
                borderColor: 'primary.main'
              }
            }}
          >
            <input {...getInputProps()} />
            <Stack alignItems="center" spacing={2}>
              <Upload fontSize="large" color="primary" />
              <Typography variant="h6">
                {isDragActive ? 'Drop the Excel file here' : 'Click or drag Excel file here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports .xlsx, .xls, and .csv files
              </Typography>
            </Stack>
          </Box>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Processing file...</Typography>
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" icon={<Error />}>
              {error}
            </Alert>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert severity="warning" icon={<Warning />}>
              <Typography variant="subtitle2" gutterBottom>
                Validation Issues:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {validationErrors.slice(0, 10).map((err, index) => (
                  <li key={index}>
                    <Typography variant="body2">{err}</Typography>
                  </li>
                ))}
                {validationErrors.length > 10 && (
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      ... and {validationErrors.length - 10} more issues
                    </Typography>
                  </li>
                )}
              </Box>
            </Alert>
          )}

          {/* Preview */}
          {importData.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CheckCircle color="success" />
                <Typography variant="h6">
                  Preview ({importData.length} rows)
                </Typography>
                {fileName && (
                  <Chip label={fileName} size="small" variant="outlined" />
                )}
              </Stack>

              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Barcode</TableCell>
                      <TableCell>Quantity</TableCell>
                      {importData.some(r => r.source) && <TableCell>Source</TableCell>}
                      {importData.some(r => r.destination) && <TableCell>Destination</TableCell>}
                      {importData.some(r => r.boxBarcode) && <TableCell>Box Barcode</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importData.slice(0, 50).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {row.barcode}
                        </TableCell>
                        <TableCell>{row.quantity}</TableCell>
                        {importData.some(r => r.source) && (
                          <TableCell>{row.source || '-'}</TableCell>
                        )}
                        {importData.some(r => r.destination) && (
                          <TableCell>{row.destination || '-'}</TableCell>
                        )}
                        {importData.some(r => r.boxBarcode) && (
                          <TableCell>{row.boxBarcode || '-'}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {importData.length > 50 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            ... and {importData.length - 50} more rows
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importData.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
        >
          {loading ? 'Importing...' : `Import ${importData.length} Items`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImportDialog;